import React from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
import { PricingTableFormValues, BundleValues } from "@/schemas/pricingTableSchema";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FormControl, FormField, FormItem } from '@/components/ui/form';
import { Trash2, Plus, Users, HeartHandshake, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast } from '@/hooks/use-toast';

interface PricingTableBundlesProps {
  feesApportionParties: any[];
  documentTypes: any[];
}

export const PricingTableBundles: React.FC<PricingTableBundlesProps> = ({
  feesApportionParties,
  documentTypes
}) => {
  const { control, watch, setValue } = useFormContext<PricingTableFormValues>();
  const [selectedBundleIndex, setSelectedBundleIndex] = React.useState<number | null>(null);
  const [selectedDocumentTypeId, setSelectedDocumentTypeId] = React.useState<string>("");
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);

  const {toast} = useToast()

  const {
    fields: bundleFields,
    append: appendBundle,
    remove: removeBundle,
    update: updateBundle
  } = useFieldArray({
    control,
    name: "bundles"
  });

  const handleAddBundle = () => {
    // Generate a new bundle ID
    const newBundleId = bundleFields.length + 1;
    
    // Create default apportionment amounts
    const defaultApportions = feesApportionParties.map(party => ({
      apportionment_party_id: party.id,
      amount: 0
    }));
    
    const newBundle: BundleValues = {
      name: `Bundle ${newBundleId}`,
      multiply_by_applicants: false,
      married_count_as_one: false,
      amount: 0,
      timing: 'upfront',
      documents: [],
      apportionment: defaultApportions
    };
    
    appendBundle(newBundle);
  };

  const handleRemoveBundle = (bundleIndex: number) => {
    removeBundle(bundleIndex);
  };

  const handleAddDocumentToBundle = (bundleIndex: number) => {
    setSelectedBundleIndex(bundleIndex);
    setSelectedDocumentTypeId("");
    setIsDialogOpen(true);
  };

  const handleConfirmAddDocument = () => {
    if (selectedBundleIndex === null || !selectedDocumentTypeId) {
      toast({
        title: "Please select a document type",
        variant: "destructive"
      })
      return;
    }

    const selectedDocumentType = documentTypes.find(dt => dt.id === parseInt(selectedDocumentTypeId));
    if (!selectedDocumentType) {
      toast({
        title: "Selected document type not found",
        variant: "destructive"
      })
      return;
    }

    const currentBundle = bundleFields[selectedBundleIndex];
    
    // Check if document type is already in the bundle
    const existingDocument = currentBundle.documents.find(doc => doc.document_type_id === selectedDocumentType.id);
    if (existingDocument) {
      toast({
        title: "This document type is already in the bundle",
        variant: "destructive"
      })
      return;
    }

    const newDocument = {
      document_type_id: selectedDocumentType.id,
      document_type_name: selectedDocumentType.name
    };
    
    const updatedDocuments = [...currentBundle.documents, newDocument];
    
    updateBundle(selectedBundleIndex, {
      ...currentBundle,
      documents: updatedDocuments
    });
    
    setIsDialogOpen(false);
    setSelectedDocumentTypeId("");
    setSelectedBundleIndex(null);
  };

  const handleRemoveDocumentFromBundle = (bundleIndex: number, documentIndex: number) => {
    const currentBundle = bundleFields[bundleIndex];
    const updatedDocuments = currentBundle.documents.filter((_, index) => index !== documentIndex);
    
    updateBundle(bundleIndex, {
      ...currentBundle,
      documents: updatedDocuments
    });
    
  };

  const updateBundleApportionmentAmount = (bundleIndex: number, partyId: number, value: string) => {
    const numValue = value === '' ? 0 : parseFloat(value);
    
    const watchedApportionments = watch(`bundles.${bundleIndex}.apportionment`);
    if (!Array.isArray(watchedApportionments)) {
      console.warn("Watched apportionments is not an array:", watchedApportionments);
      setValue(`bundles.${bundleIndex}.apportionment`, []);
    }
    
    const currentApportionments = [...(Array.isArray(watchedApportionments) ? watchedApportionments : [])];
    
    const apportionmentIndex = currentApportionments.findIndex(a => 
      a && a.apportionment_party_id === partyId
    );
    
    if (apportionmentIndex >= 0) {
      const updatedApportionments = [...currentApportionments];
      updatedApportionments[apportionmentIndex] = {
        ...updatedApportionments[apportionmentIndex],
        amount: numValue
      };
      setValue(`bundles.${bundleIndex}.apportionment`, updatedApportionments);
    } else {
      const newApportionment = [
        ...currentApportionments, 
        {
          apportionment_party_id: partyId,
          amount: numValue
        }
      ];
      setValue(`bundles.${bundleIndex}.apportionment`, newApportionment);
    }
    
    setTimeout(() => {
      validateBundleTotal(bundleIndex);
    }, 100);
  };

  const validateBundleTotal = (bundleIndex: number) => {
    const apportionments = watch(`bundles.${bundleIndex}.apportionment`);
    if (!Array.isArray(apportionments)) {
      console.warn("Apportionments for validation is not an array:", apportionments);
      return;
    }
    
    const bundleAmount = watch(`bundles.${bundleIndex}.amount`) || 0;
    const total = apportionments.reduce((sum, item) => sum + Number(item?.amount || 0), 0);
    
  };

  const getBundleApportionmentAmount = (apportionments: any[] = [], partyId: number) => {
    if (!Array.isArray(apportionments)) {
      console.warn("apportionments is not an array:", apportionments);
      return 0;
    }
    const apportionment = apportionments.find(a => a.apportionment_party_id === partyId);
    return apportionment ? apportionment.amount || 0 : 0;
  };

  return (
    <TooltipProvider>
      <div className="rounded-md border shadow-sm bg-white">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold">Bundles</h3>
            <p className="text-sm text-muted-foreground">Configure bundled document packages</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">
              {bundleFields.length} {bundleFields.length === 1 ? 'bundle' : 'bundles'}
            </span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={handleAddBundle}
            >
              <Plus className="h-3 w-3 mr-1" />
              Add Bundle
            </Button>
          </div>
        </div>
        <div className="p-4">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Bundle Name</TableHead>
                  <TableHead>Document Names</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead className="w-24 text-center">Options</TableHead>
                  {feesApportionParties.map(party => (
                    <TableHead key={party.id}>
                      {party.description || party.entity_name}
                    </TableHead>
                  ))}
                  <TableHead className="w-16">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bundleFields.map((bundleField, bundleIndex) => (
                  <TableRow key={`bundle-${bundleField.id}`}>
                    <TableCell>
                      <FormField
                        control={control}
                        name={`bundles.${bundleIndex}.name`}
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input 
                                className="h-8"
                                placeholder="Bundle name"
                                {...field}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </TableCell>
                    
                    <TableCell>
                      <div className="space-y-2">
                        <div className="flex flex-wrap gap-2">
                          {bundleField.documents.length > 0 ? (
                            bundleField.documents.map((doc, docIndex) => (
                              <Badge 
                                key={docIndex} 
                                variant="default"
                                className="flex items-center gap-1 pr-1"
                              >
                                <span className="text-xs">{doc.document_type_name}</span>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRemoveDocumentFromBundle(bundleIndex, docIndex)}
                                  className="h-4 w-4 p-0 hover:bg-destructive hover:text-destructive-foreground rounded-full"
                                >
                                  <X className="h-3 w-3" />
                                  <span className="sr-only">Remove {doc.document_type_name}</span>
                                </Button>
                              </Badge>
                            ))
                          ) : (
                            <div className="text-sm text-gray-400 italic">No documents added</div>
                          )}
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleAddDocumentToBundle(bundleIndex)}
                          className="text-xs h-6 px-2"
                        >
                          <Plus className="h-3 w-3 mr-1" />
                          Add Document
                        </Button>
                      </div>
                    </TableCell>
                    
                    <TableCell>
                      <FormField
                        control={control}
                        name={`bundles.${bundleIndex}.amount`}
                        render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <div className="flex items-center justify-center space-x-1">
                                <span className="text-gray-500">$</span>
                                <Input 
                                  type="number"
                                  min="0"
                                  placeholder="0.00"
                                  className="amount-input"
                                  style={{ appearance: "textfield" }}
                                  value={field.value === undefined || field.value === null ? 0 : field.value}
                                  onChange={(e) => {
                                    const value = e.target.value === '' ? 0 : parseFloat(e.target.value);
                                    field.onChange(isNaN(value) ? 0 : value);
                                  }}
                                />
                              </div>
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </TableCell>
                    
                    <TableCell>
                      <div className="flex items-center justify-center space-x-2">
                        <FormField
                          control={control}
                          name={`bundles.${bundleIndex}.multiply_by_applicants`}
                          render={({ field }) => (
                            <FormItem className="flex flex-col items-center space-y-1">
                              <FormControl>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="flex flex-col items-center">
                                      <Switch
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                      />
                                      <Users 
                                        size={14} 
                                        className={field.value ? "text-blue-500" : "text-gray-300"} 
                                      />
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Multiply by Applicants</p>
                                  </TooltipContent>
                                </Tooltip>
                              </FormControl>
                            </FormItem>
                          )}
                        />
                        
                        <FormField
                          control={control}
                          name={`bundles.${bundleIndex}.married_count_as_one`}
                          render={({ field }) => (
                            <FormItem className="flex flex-col items-center space-y-1">
                              <FormControl>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="flex flex-col items-center">
                                      <Switch
                                        checked={field.value}
                                        onCheckedChange={field.onChange}
                                      />
                                      <HeartHandshake
                                        size={14} 
                                        className={field.value ? "text-purple-500" : "text-gray-300"} 
                                      />
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Married Count as One</p>
                                  </TooltipContent>
                                </Tooltip>
                              </FormControl>
                            </FormItem>
                          )}
                        />
                      </div>
                    </TableCell>
                    
                    {feesApportionParties.map(party => (
                      <TableCell key={party.id}>
                        <div className="flex items-center justify-center space-x-1">
                          <span className="text-gray-500">$</span>
                          <Input
                            type="number"
                            min="0"
                            className="apportion-input"
                            style={{ appearance: "textfield" }}
                            value={getBundleApportionmentAmount(watch(`bundles.${bundleIndex}.apportionment`) || [], party.id)}
                            onChange={(e) => updateBundleApportionmentAmount(bundleIndex, party.id, e.target.value)}
                          />
                        </div>
                      </TableCell>
                    ))}
                    
                    <TableCell>
                      <div className="flex space-x-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveBundle(bundleIndex)}
                          className="text-red-500 h-8 w-8 p-0"
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="sr-only">Remove bundle</span>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                
                {bundleFields.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5 + feesApportionParties.length} className="text-center py-6 text-gray-500">
                      No bundles defined. Click "Add Bundle" to create your first bundle.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      {/* Add Document Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add Document to Bundle</DialogTitle>
            <DialogDescription>
              Select a document type to add to the bundle.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <label htmlFor="document-type" className="text-right">
                Document Type
              </label>
              <div className="col-span-3">
                <Select value={selectedDocumentTypeId} onValueChange={setSelectedDocumentTypeId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a document type" />
                  </SelectTrigger>
                  <SelectContent>
                    {documentTypes.map((docType) => (
                      <SelectItem key={docType.id} value={docType.id.toString()}>
                        {docType.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button type="button" onClick={handleConfirmAddDocument}>
              Add Document
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}; 